import type { PageGlobalProps } from '../interfaces';
import useCustomer from 'stores/customer';
import useDrawer from 'stores/drawer';
import { Navigate, Outlet, useLocation, type NavigateProps } from 'react-router-dom';
import usePos from 'stores/pos';
import { Flex, Footer, Toaster } from 'front-commons/ds';
import { useMemo } from 'react';
import pages from '../pages';
import NavigationHeader from 'containers/Headers/NavigationHeader';

export default function RouteProtection({
	type,
	headerType,
	showFooter,
	needContext,
	stickyTopRow = false,
	showHelpButton,
	underRestriction,
	allowedRestrictions,
}: PageGlobalProps) {
	const location = useLocation();

	const {
		customerStore: { token, loading: customerDataLoading },
		customerRestrictionRedirect,

		handleSetLogout,
		mainCustomerPage,
		getRestrictionType,
	} = useCustomer();
	const {
		posStore: { selectedPos },
	} = usePos();

	const defaultPage = mainCustomerPage();
	const customerRestriction = getRestrictionType();

	const handleWithStartedFrom = (props: NavigateProps): NavigateProps | void => {
		if (location.state.startedFrom !== location.pathname) props.state = { ...props.state, ...location.state };

		if (customerRestriction || !token) return;

		const redirectPage = Object.values(pages).find((page) => page.pathname === location.state.startedFrom);
		if (redirectPage?.needContext?.includes('POS') && !selectedPos?.id) return;

		return { to: location.state.startedFrom };
	};

	const navigateProps = useMemo((): NavigateProps | undefined => {
		const props = {} as NavigateProps;

		if (location?.state?.startedFrom) {
			const replaceCustomer = handleWithStartedFrom(props);
			if (replaceCustomer) return replaceCustomer;
		}

		if (customerDataLoading || type === 'PUBLIC') return undefined;

		if (type === 'NOT_LOGGED' && token) {
			const logout = () => {
				handleSetLogout(true);

				return undefined;
			};

			if (customerRestriction === 'QUOTATION' || location.state?.logout) return logout();

			if (location.pathname === '/acesso-distribuidor') {
				return undefined;
			}

			props.to = defaultPage;
			props.state = location.state;
			return props;
		}

		if (
			(type === 'PRIVATE' && !token && !allowedRestrictions?.includes('QUOTATION')) ||
			(allowedRestrictions?.includes('QUOTATION') && customerRestriction !== 'QUOTATION' && !token)
		) {
			props.to = '/login';

			if (location.search) props.to += location.search;
			if (location.pathname !== '/') {
				props.state = { ...props.state, startedFrom: location.pathname };
			}

			return props;
		}

		if (needContext === 'POS' && !selectedPos?.id) {
			props.to = defaultPage;
			props.state = { ...props.state, startedFrom: location.pathname };
			return props;
		}

		if (customerRestriction) {
			if (underRestriction && customerRestriction === underRestriction) return undefined;

			if (allowedRestrictions && allowedRestrictions.includes(customerRestriction)) return undefined;

			props.to = customerRestrictionRedirect[customerRestriction];
			return props;
		}

		if (underRestriction && !customerRestriction) {
			props.to = defaultPage;
			return props;
		}

		return undefined;
	}, [location.pathname, customerDataLoading]);

	if (navigateProps && location.pathname !== navigateProps.to) return <Navigate replace {...navigateProps} />;

	return (
		<ContentWrapper
			headerType={headerType}
			showFooter={showFooter}
			stickyTopRow={stickyTopRow}
			showHelpButton={showHelpButton}
		/>
	);
}

function ContentWrapper({
	headerType,
	showFooter = true,
	stickyTopRow,
	showHelpButton,
}: Pick<PageGlobalProps, 'headerType' | 'showFooter' | 'stickyTopRow' | 'showHelpButton'>) {
	const { drawerStore } = useDrawer();

	const hasNotAnyOpenedDrawer = useMemo(() => {
		return !drawerStore.drawers.filter((drawer) => drawer.open).length;
	}, [drawerStore.drawers]);

	return (
		<>
			<Flex display="initial" position="sticky" top="0" zIndex={100}>
				{headerType && headerType !== 'hide' && (
					<NavigationHeader headerType={headerType} stickyTopRow={stickyTopRow} showHelpButton={showHelpButton} />
				)}

				{hasNotAnyOpenedDrawer && <Toaster target="desktop" />}
			</Flex>

			{hasNotAnyOpenedDrawer && <Toaster target="mobile" />}

			<Outlet />

			{showFooter && (
				<>
					<Flex margin={{ small: '0 0 calc(56px + 33px)', medium: '0 0 120px' }} />
					<Footer />
					<Flex margin="0 0 var(--promotion-details-action-bar-height)" />
				</>
			)}
		</>
	);
}
